import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import 'whatwg-fetch';

import errors from '../errors';
import Config from '../site.config.json';

import * as MessageActions from '../actions/message';

import { Dispatch } from 'redux';

const restReadUrl = Config.tsqPath + '/tsq/ui/common/resources/rest/json/read';
const restUpdateUrl =
	Config.tsqPath + '/tsq/ui/common/resources/rest/json/update';
const restCreateUrl =
	Config.tsqPath + '/tsq/ui/common/resources/rest/json/create';
const restDeleteUrl =
	Config.tsqPath + '/tsq/ui/common/resources/rest/json/delete';

const TIMEOUT_MSEC = 61000;

export interface ITsqErrorResponse {
	name?: never;
	result: string;
	messageKey: string;
	titleKey: string;
	resultDialogTitle?: string;
	resultDialogMessage?: string;
}

export interface IPwrErrorResponse {
	result: 'failed';
	messageKey: string;
	titleKey: string;
	resultDialogTitle?: string;
	resultDialogMessage?: string;
}

export interface IOptions {
	[index: string]:
		| string
		| string[]
		| number
		| number[]
		| boolean
		| boolean[]
		| IOptions
		| IOptions[]
		| null
		| undefined;
}

const defaultOptions: IOptions = {
	credentials: 'include',
	headers: {
		'Content-Type': 'application/json',
	},
	method: 'POST',
};

/**
 * fetchオプションをマージする
 * @param {string} modelId モデルID
 * @param {Map} data データ
 */
const mergeOptions = (
	modelId: string,
	token: string,
	data: IOptions
): IOptions => {
	// データを整形
	const newData = {
		__tsqModelId: modelId,
		__tsqSecureToken: token,
	};
	data = data || {};
	newData[modelId] = data;

	// オプション生成
	return {
		...defaultOptions,
		body: JSON.stringify(newData),
	};
};

/**
 * 「パスワード再発行」向けパスワードリマインダの検証要素
 */
// tslint:disable: object-literal-sort-keys
export const pwrVerificationTypesForPwremind = {
	/** 「姓」 */
	PWREMIND_SHOW_FAMILYNAME: 'PWREMIND_SHOW_FAMILYNAME',
	/** 「姓＋名」（姓と名の間は全角スペース） */
	PWREMIND_SHOW_FULLNAME: 'PWREMIND_SHOW_FULLNAME',
	/** 「生年月日」 */
	PWREMIND_SHOW_BIRTHDAY: 'PWREMIND_SHOW_BIRTHDAY',
	/** 「E-Mail」 */
	PWREMIND_SHOW_EMAIL: 'PWREMIND_SHOW_EMAIL',
	/** 「本務ユーザ属性1」 */
	PWREMIND_SHOW_USERATTRIBUTE1: 'PWREMIND_SHOW_USERATTRIBUTE1',
	/** 「本務ユーザ属性2」 */
	PWREMIND_SHOW_USERATTRIBUTE2: 'PWREMIND_SHOW_USERATTRIBUTE2',
	/** 「本務ユーザ属性3」 */
	PWREMIND_SHOW_USERATTRIBUTE3: 'PWREMIND_SHOW_USERATTRIBUTE3',
	/** 「本務ユーザ属性4」 */
	PWREMIND_SHOW_USERATTRIBUTE4: 'PWREMIND_SHOW_USERATTRIBUTE4',
} as const;
// tslint:enable: object-literal-sort-keys

/**
 * パスワードリマインダの検証要素型
 */
export type PwrIdentificationItemTypeForPwremind = keyof typeof pwrVerificationTypesForPwremind;

/**
 * 「ログインID通知およびパスワード再発行」向けパスワードリマインダの検証要素
 */
// tslint:disable: object-literal-sort-keys
export const pwrVerificationTypesForIdremind = {
	/** 「姓」 */
	IDREMIND_SHOW_FAMILYNAME: 'IDREMIND_SHOW_FAMILYNAME',
	/** 「姓＋名」（姓と名の間は全角スペース） */
	IDREMIND_SHOW_FULLNAME: 'IDREMIND_SHOW_FULLNAME',
	/** 「生年月日」 */
	IDREMIND_SHOW_BIRTHDAY: 'IDREMIND_SHOW_BIRTHDAY',
	/** 「本務ユーザ属性1」 */
	IDREMIND_SHOW_USERATTRIBUTE1: 'IDREMIND_SHOW_USERATTRIBUTE1',
	/** 「本務ユーザ属性2」 */
	IDREMIND_SHOW_USERATTRIBUTE2: 'IDREMIND_SHOW_USERATTRIBUTE2',
	/** 「本務ユーザ属性3」 */
	IDREMIND_SHOW_USERATTRIBUTE3: 'IDREMIND_SHOW_USERATTRIBUTE3',
	/** 「本務ユーザ属性4」 */
	IDREMIND_SHOW_USERATTRIBUTE4: 'IDREMIND_SHOW_USERATTRIBUTE4',
} as const;
// tslint:enable: object-literal-sort-keys

/**
 * パスワードリマインダの検証要素型
 */
export type PwrIdentificationItemTypeForIdremind = keyof typeof pwrVerificationTypesForIdremind;

/**
 * パスワードリマインダの検証要素
 */
export const pwrVerificationTypes = {
	...pwrVerificationTypesForPwremind,
	...pwrVerificationTypesForIdremind,
} as const;

/**
 * パスワードリマインダの検証要素型
 */
export type PwrIdentificationItemType = keyof typeof pwrVerificationTypes;

/**
 * 「ログインID通知およびパスワード再発行」向け
 */
export const filterIdentificationItemTypeForPwremind = (
	type: PwrIdentificationItemType
): type is PwrIdentificationItemTypeForPwremind =>
	type && typeof type === 'string' && type.startsWith('PWREMIND_');

/**
 * 「ログインID通知およびパスワード再発行」向け
 */
export const filterIdentificationItemTypeForIdremind = (
	type: PwrIdentificationItemType
): type is PwrIdentificationItemTypeForIdremind =>
	type && typeof type === 'string' && type.startsWith('IDREMIND_');

/**
 * ログイン画面初期化時のAPIレスポンス型
 */
export interface ILoginInitPayload {
	result?: never;
	carouselChangeInterval: string;
	lmcmDisplayManagerNoticeMessages: Array<{
		contact: string;
		message: string[];
		startDate: string;
		titleList: string[];
	}>;
	multiTenantMode: string;
	/**
	 * パスワードリマインダ
	 */
	pwr: {
		/**
		 * ログインIDのリマインドを許すか
		 */
		loginIdLinkEnabled: boolean;
		/**
		 * パスワードのリマインドを許すか
		 */
		passwordLinkEnabled: boolean;
		/**
		 * 検証項目の一覧: 設定から取得
		 */
		identificationItems: PwrIdentificationItemType[];
	};
	/**
	 * CM表示制御
	 */
	showCMOption: boolean;
	/**
	 * 言語表示制御
	 */
	showLangOption: boolean;
}

export interface IChangePasswordPayload {
	result: 'succeeded';
	loginID: string;
}

const Transfers = {
	read: (modelId: string, token: string, data: IOptions = {}): Promise<any> =>
		Transfers.restCore(restReadUrl, modelId, token, data),

	update: (
		modelId: string,
		token: string,
		data: IOptions = {}
	): Promise<any> => Transfers.restCore(restUpdateUrl, modelId, token, data),

	create: (
		modelId: string,
		token: string,
		data: IOptions = {}
	): Promise<any> => Transfers.restCore(restCreateUrl, modelId, token, data),

	delete: (
		modelId: string,
		token: string,
		data: IOptions = {}
	): Promise<any> => Transfers.restCore(restDeleteUrl, modelId, token, data),

	/**
	 * restの共通処理
	 *
	 * @param {string} modelId モデルID
	 * @param {Map} data データ
	 */
	restCore: (
		url: string,
		modelId: string,
		token: string,
		data: IOptions
	): Promise<any> => {
		const options = mergeOptions(modelId, token, data);
		return fetch(url, options);
	},

	/**
	 * パスワードリマインダの送信処理
	 * @param companyCode 法人コード
	 * @param email メールアドレス(ログインID通知およびパスワード再発行)
	 * @param inputValue 選択する法人確認項目を対応する入力値
	 * @param loginID ログインID(パスワード再発行)
	 * @param type 選択する法人確認項目
	 */
	fetchSendMail: async (
		email: string,
		inputValue: string,
		loginID: string,
		type: string,
		companyCode?: string
	): Promise<IChangePasswordPayload> => {
		const abortController = new AbortController();
		const timer = window.setTimeout(() => {
			abortController.abort();
		}, TIMEOUT_MSEC);
		return fetch(Config.tsqPath + '/tsq-api/pwrSendMail.action', {
			body: JSON.stringify({
				companyCode,
				email,
				inputValue,
				loginID,
				type,
			}),
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			method: 'POST',
			signal: abortController.signal,
		})
			.then(async response => {
				clearTimeout(timer);
				if (!response.ok) {
					throw new errors.ServerSystem();
				}
				const data:
					| IChangePasswordPayload
					| IPwrErrorResponse = await response.json();
				if (data.result !== 'succeeded') {
					throw data;
				} else {
					return data;
				}
			})
			.catch((e: Error | ITsqErrorResponse) => {
				if (e.name === 'AbortError') {
					const t: ITsqErrorResponse = {
						messageKey: 'LMCM_E_00714',
						result: 'failed',
						titleKey: 'COM_D_0001',
					};
					throw t;
				} else if (e.name !== void 0) {
					const t: ITsqErrorResponse = {
						messageKey: 'COM_F_0001', // システムエラー
						result: 'failed',
						titleKey: 'COM_D_0001', // システムエラー
					};
					throw t;
				} else {
					throw e;
				}
			});
	},

	/**
	 * パスワードリマインダのパスワード変更画面の初期表示処理
	 * @param authKey 認証キー
	 * @param companyCode 法人コード
	 */
	fetchChangePasswordInit: async (
		authKey: string,
		companyCode?: string
	): Promise<IChangePasswordPayload> => {
		return fetch(Config.tsqPath + '/tsq-api/pwrpasswordChangeInit.action', {
			body: JSON.stringify({ authKey, companyCode }),
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			method: 'POST',
		}).then(async response => {
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			const data:
				| IChangePasswordPayload
				| IPwrErrorResponse = await response.json();
			if (data.result !== 'succeeded') {
				throw data;
			} else {
				return data;
			}
		});
	},

	/**
	 * パスワードリマインダのパスワード変更画面の登録処理
	 * @param authKey 認証キー
	 * @param changedPassword 変更後パスワード
	 * @param companyCode 法人コード
	 */
	fetchChangePassword: async (
		authKey: string,
		changedPassword: string,
		companyCode?: string
	): Promise<IChangePasswordPayload> => {
		const AbortController = window.AbortController;
		const abortController = new AbortController();
		const timer = window.setTimeout(() => {
			abortController.abort();
		}, TIMEOUT_MSEC);
		return fetch(Config.tsqPath + '/tsq-api/pwrpasswordChange.action', {
			body: JSON.stringify({
				authKey,
				changedPassword,
				companyCode,
			}),
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			method: 'POST',
			signal: abortController.signal,
		})
			.then(async response => {
				clearTimeout(timer);
				if (!response.ok) {
					throw new errors.ServerSystem();
				}
				const data:
					| IChangePasswordPayload
					| IPwrErrorResponse = await response.json();
				if (data.result !== 'succeeded') {
					throw data;
				} else {
					return data;
				}
			})
			.catch((e: Error | ITsqErrorResponse) => {
				if (e.name === 'AbortError') {
					const t: ITsqErrorResponse = {
						messageKey: 'LMCM_E_00714',
						result: 'failed',
						titleKey: 'COM_D_0001',
					};
					throw t;
				} else if (e.name !== void 0) {
					const t: ITsqErrorResponse = {
						messageKey: 'COM_F_0001', // システムエラー
						result: 'failed',
						titleKey: 'COM_D_0001', // システムエラー
					};
					throw t;
				} else {
					throw e;
				}
			});
	},

	/**
	 * ログイン画面表示用データ類を取得
	 *
	 * @param companyCode 法人コード
	 */
	fetchManagerNoticeMessages: async (
		companyCode: string | undefined
	): Promise<ILoginInitPayload> => {
		return fetch(Config.tsqPath + '/tsq-api/loginInit.action', {
			body: JSON.stringify({ companyCode }),
			method: 'POST',
			mode: 'cors',
		}).then(async response => {
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			const data:
				| ILoginInitPayload
				| ITsqErrorResponse = await response.json();

			if (data.result !== undefined) {
				throw data;
			} else {
				return data;
			}
		});
	},

	login: (
		companyCode: string,
		userName: string,
		password: string
	): Promise<any> => {
		const formData = new FormData();
		if (Config.multiTenant) {
			formData.append('companyCode', companyCode);
		} else {
			formData.append('companyCode', '');
		}
		formData.append('username', userName);
		formData.append('password', password);
		const abortController = new AbortController();
		const timer = window.setTimeout(() => {
			abortController.abort();
		}, TIMEOUT_MSEC);
		// 認証
		return fetch(Config.tsqPath + '/tsq-api/restlogin.action', {
			body: formData,
			credentials: 'include',
			method: 'POST',
			mode: 'cors',
			signal: abortController.signal,
		}).then(response => {
			clearTimeout(timer);
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},

	loginSso: (): Promise<any> => {
		const formData = new FormData();
		const abortController = new AbortController();
		const timer = window.setTimeout(() => {
			abortController.abort();
		}, TIMEOUT_MSEC);
		// 認証
		return fetch(Config.tsqPath + '/tsq-sso/restSsologin.action', {
			body: formData,
			credentials: 'include',
			method: 'POST',
			mode: 'cors',
			signal: abortController.signal,
		}).then(response => {
			clearTimeout(timer);
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},

	getUserInfoOrgList: (): Promise<any> => {
		return fetch(
			Config.tsqPath + '/tsq/ui/common/auth/userInfo/getOrgList.action',
			{
				credentials: 'include',
				method: 'POST',
			}
		).then(response => {
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},

	getUserInfoOrgInfo: (orgId: string): Promise<any> => {
		const formData = new FormData();
		formData.append('orgId', orgId);
		return fetch(
			Config.tsqPath + '/tsq/ui/common/auth/userInfo/setOrgInfo.action',
			{
				body: formData,
				credentials: 'include',
				method: 'POST',
			}
		).then(response => {
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},

	getLmRdbUserInfoOrgList: (
		dispatch: Dispatch,
		nonce: string
	): Promise<any> => {
		const formData = new FormData();
		formData.append('nonce', nonce);
		return fetch(
			Config.tsqPath +
				'/tsq/ui/common/auth/userInfo/getLmRdbOrgList.action',
			{
				body: formData,
				credentials: 'include',
				method: 'POST',
			}
		).then(response => {
			if (!response.ok) {
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content: 'COM_F_0001',
							},
						],
						title: 'COM_D_0001',
					})
				);
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},

	getLmRdbUserInfoOrgInfo: (
		dispatch: Dispatch,
		orgId: string,
		nonce: string
	): Promise<any> => {
		const formData = new FormData();
		formData.append('orgId', orgId);
		formData.append('nonce', nonce);
		return fetch(
			Config.tsqPath +
				'/tsq/ui/common/auth/userInfo/setLmRdbOrgInfo.action',
			{
				body: formData,
				credentials: 'include',
				method: 'POST',
			}
		).then(response => {
			if (!response.ok) {
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content: 'COM_F_0001',
							},
						],
						title: 'COM_D_0001',
					})
				);
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},

	getLoginUserInfoModel: (): Promise<any> => {
		return fetch(restReadUrl, {
			body: JSON.stringify({
				__tsqModelId: 'tsq_common_LoginUserInfoModel',
				tsq_common_LoginUserInfoModel: {},
			}),
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			method: 'POST',
		}).then(response => {
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},

	logout: (): Promise<any> => {
		return fetch(Config.tsqPath + '/tsq-api/logout.action', {
			credentials: 'include',
			method: 'POST',
		});
	},

	samlStart: (companyCode: string): Promise<any> => {
		const body = JSON.stringify({ companyCode });
		return fetch(Config.tsqPath + '/tsq-api/samlStart.action', {
			body,
			method: 'POST',
			mode: 'cors',
		}).then(response => {
			if (!response.ok) {
				throw new errors.ServerSystem();
			}
			return response.json();
		});
	},
};

export default Transfers;



// WEBPACK FOOTER //
// ./src/utils/Transfers.ts